import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { admin, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("expired") === "1";
    const flag = sessionStorage.getItem("immersion_session_expired") === "1";
    if (q || flag) {
      setExpired(true);
      sessionStorage.removeItem("immersion_session_expired");
    }
  }, []);

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-background grain flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display font-extrabold text-2xl uppercase tracking-tighter block mb-2">Immersion Festival</Link>
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-10">Admin Access</p>

        {expired && <p data-testid="session-expired-note" className="text-[#e3c67d] text-xs uppercase tracking-[0.12em] mb-8 -mt-4">Your session expired — please sign in again</p>}

        <form onSubmit={submit} className="space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Email</label>
            <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Password</label>
            <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-[#C44536] text-xs uppercase tracking-[0.1em]">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in</> : "Sign In"}
          </button>
        </form>
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-white transition-colors mt-10 inline-block">← Back to site</Link>
      </div>
    </div>
  );
}
